import css from './Tap2GOModal.module.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'

import { IGoodServiceTicketData, ITap2GOData, Tap2GOStatus } from 'meta/pages/goodsandservices'
import { ApiEndpoint } from 'controller/endpoint'
import { TICKETSMS_COLORS_LIGHTGREY, TICKETSMS_COLORS_MEDIUMGREY } from 'utils/constants/colors'

import { ButtonWhiteBlack } from 'components/Actions/Action'
import Img from 'components/Media/Img'
import { useBackendApiBase } from 'hooks/useEnv'

import BarTimer from './BarTimer'
import DraggableBox from './DraggableBox'

interface Props {
  onClose(): void
  ticketData: IGoodServiceTicketData
  initialUuid: string
}

const Tap2GoModal: React.FC<Props> = (props) => {
  const { onClose, ticketData, initialUuid } = props

  const { t } = useTranslation()
  const backendApiBase = useBackendApiBase()

  const [status, setStatus] = useState<Tap2GOStatus>(Tap2GOStatus.request)

  const [initialTime, setInitialTime] = useState<number>(0)
  const [currentTime, setCurrentTime] = useState<number>(0)

  const [currentTryout, setCurrentTryout] = useState<number>(0)

  const [currentUuid, setCurrentUuid] = useState<string>(initialUuid)

  const [tap2GoRequestData, setTap2GoRequestData] = useState<ITap2GOData | null>(null)
  const [tap2GoFailureMsg, setTap2GoFailureMsg] = useState<string>('')

  useEffect(() => {
    if (!backendApiBase || tap2GoRequestData) return

    if (currentTryout >= 5) {
      onClose()
    }

    axios
      .post(`${backendApiBase}${ApiEndpoint.vault.request()}`, {
        secret: ticketData.secret,
        requestCode: currentUuid,
        eventId: ticketData.event.id,
      })
      .then((response) => {
        // TODO set response data
        setTap2GoRequestData(response.data.data)

        setCurrentTryout((prevTryout) => {
          return prevTryout + 1
        })
      })
      .catch((error) => {
        setStatus(Tap2GOStatus.failure)

        if (!navigator.onLine) {
          setTap2GoFailureMsg(String(t('goodsAndServices:tap2go_errors_offline')))
        } else {
          setTap2GoFailureMsg(error.response.data.error)
        }
      })
  }, [backendApiBase, currentTryout, tap2GoRequestData])

  useEffect(() => {
    if (status == Tap2GOStatus.start) {
      const timerInterval = setInterval(() => {
        setCurrentTime((prevTime) => {
          if (prevTime === 0) {
            setStatus(Tap2GOStatus.request)
            return 0
          } else {
            return prevTime - 1
          }
        })
      }, 1000)

      return () => clearInterval(timerInterval)
    }
  }, [status])

  const getDifferenceInSeconds = (from: number, to: number): number => {
    const isMilliseconds = from > 1e12 || to > 1e12
    if (isMilliseconds) {
      return Math.floor((to - from) / 1000)
    }
    return to - from
  }

  useEffect(() => {
    if (!tap2GoRequestData) return

    const initialTime = getDifferenceInSeconds(tap2GoRequestData.otp.validFrom, tap2GoRequestData.otp.validTo)

    setInitialTime(initialTime)
    setCurrentTime(initialTime - ((tap2GoRequestData.otp.timeElapsed / 1000) | 0))

    setStatus(Tap2GOStatus.start)
  }, [tap2GoRequestData])

  useEffect(() => {
    if (status == Tap2GOStatus.request) {
      setTap2GoRequestData(null)
    }
  }, [status])

  useEffect(() => {
    if (status == Tap2GOStatus.check) {
      axios
        .post(`${backendApiBase}${ApiEndpoint.vault.check()}`, {
          otp: tap2GoRequestData?.otp.value,
          secret: ticketData.secret,
          requestCode: currentUuid,
          eventId: ticketData.event.id,
        })
        .then(() => {
          setStatus(Tap2GOStatus.success)
        })
        .catch((error) => {
          setStatus(Tap2GOStatus.failure)

          if (!navigator.onLine) {
            setTap2GoFailureMsg(String(t('goodsAndServices:tap2go_errors_offline')))
          } else {
            setTap2GoFailureMsg(error.response.data.error)
          }
        })
    }
  }, [status])

  const handleCheck = () => {
    setStatus(Tap2GOStatus.check)
  }

  return (
    <div className={css.outer_wrapper}>
      <div className={css.wrapper}>
        <div className={css.header}>
          <div className={css.title}>{t('goodsAndServices:tap2Go')}</div>
          <div
            className={css.close}
            onClick={() => {
              if (status == Tap2GOStatus.success) {
                window.location.reload()
              }

              onClose()
            }}
          >
            <Img src="/icons/close_icon_white_smallcross.svg" />
          </div>
        </div>

        <div className={css.ticketInfo}>
          <div className={css.left}>
            <Img src="/goodsandservices/drink-icon.svg" />
          </div>
          <div className={css.right}>
            <div className={css.title}>{ticketData.notes}</div>
            <div
              className={css.price}
            >{`${ticketData?.totalPrice?.formatted.replace(ticketData?.totalPrice?.symbol, '').replace('.', ',')} ${ticketData?.totalPrice?.symbol}`}</div>
          </div>
        </div>

        {[Tap2GOStatus.start, Tap2GOStatus.check, Tap2GOStatus.request].includes(status) && (
          <>
            <div className={css.warning}>
              <div className={css.title}>{t('goodsAndServices:warning')}</div>
              <div className={css.details}>{t('goodsAndServices:this_operation_will')}</div>
            </div>
          </>
        )}

        {[Tap2GOStatus.start, Tap2GOStatus.check].includes(status) && tap2GoRequestData && (
          <>
            <div className={css.codeBox}>
              <div className={css.code_header}>{t('goodsAndServices:your_code')}</div>
              <div className={css.code_number}>{tap2GoRequestData?.otp.value}</div>

              <div className={css.timer}>
                <BarTimer initialTime={initialTime} currentTime={currentTime} />
              </div>

              <div className={css.showCode}>
                <div className={css.title}>{t('goodsAndServices:show_barman')}</div>
                <div className={css.description}>
                  {t('goodsAndServices:use_method')} <span className={css.red}>{t('goodsAndServices:tap2Go')}</span>{' '}
                  {t('goodsAndServices:and_let_barman')}
                </div>
              </div>

              <DraggableBox onDragComplete={handleCheck} />
            </div>
          </>
        )}

        {status == Tap2GOStatus.request && (
          <>
            <Skeleton
              className={css.content_skeleton}
              baseColor={TICKETSMS_COLORS_MEDIUMGREY}
              borderRadius={16}
              highlightColor={TICKETSMS_COLORS_LIGHTGREY}
              height={375}
            />
          </>
        )}

        {status == Tap2GOStatus.success && (
          <>
            <div className={css.successBox}>
              <Img className={css.image} src="/goodsandservices/tap2go-success.svg" />

              <div className={css.title}>{t('goodsAndServices:thanks_for_using')}</div>

              <div className={css.details}>{t('goodsAndServices:drink_card_valid_msg')}</div>
            </div>
          </>
        )}

        {status == Tap2GOStatus.failure && (
          <>
            <div className={css.failureBox}>
              <Img className={css.image} src="/goodsandservices/tap2go-error-bold.svg" />

              <ButtonWhiteBlack
                className={css.button}
                onClick={() => {
                  if (tap2GoRequestData) {
                    // set current UUID
                    setCurrentUuid(uuidv4())
                    setStatus(Tap2GOStatus.request)
                  } else {
                    window.location.reload()
                    onClose()
                  }
                }}
              >
                {tap2GoRequestData && t('goodsAndServices:button_try_again')}
                {!tap2GoRequestData && t('goodsAndServices:button_close')}
              </ButtonWhiteBlack>

              <div className={css.warning}>
                <div className={css.inner}>
                  <div className={css.title}>{t('goodsAndServices:something_went_wrong')}</div>
                  <div className={css.details}>{tap2GoFailureMsg}</div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Tap2GoModal
